import React, { useCallback, useEffect } from 'react';
import CheckboxDataTable from 'base/components/table/CheckboxDataTable';
import MainTitle from 'base/components/MainTitle';
import Header from 'base/components/Header';
import { DeleteIcon, RestoreIcon } from 'base/components/Icon';
import { getActiveSpace } from 'spaces/store/selectors';
import theme from 'theme/theme';
import RowActionButton from 'base/components/table/RowActionButton/RowActionButton';
import DocumentLink from 'documents/containers/DocumentLink/DocumentLink';
import Flex from 'base/components/Flex';
import Breadcrumb from 'base/components/Breadcrumb';
import { getFoldersById } from 'documents/store/folders/selectors';
import Button from 'base/components/Button';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useMatch } from 'react-router';
import usePrevious from 'hooks/usePrevious';
import { getDeletedDocuments } from '../../store/selectors';
import { fetchDeletedDocuments, fetchDeletedFolders, restoreDocuments } from '../../store/actions';

export default function TrashPage() {
  const dispatch = useAppDispatch();
  const space = useAppSelector(getActiveSpace);
  const documents = useAppSelector(getDeletedDocuments);
  const foldersById = useAppSelector(getFoldersById);
  const match = useMatch('/spaces/:spaceId/trash');
  const spaceId = match?.params?.spaceId;
  const prevSpaceId = usePrevious(spaceId);

  const fetchData = useCallback(() => {
    dispatch(fetchDeletedDocuments(spaceId));
    dispatch(fetchDeletedFolders(spaceId));
  }, [dispatch, spaceId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (spaceId !== prevSpaceId) {
      fetchData();
    }
  }, [spaceId, prevSpaceId, fetchData]);

  const handleRestore = useCallback(
    data => {
      dispatch(restoreDocuments([data]));
    },
    [dispatch],
  );

  const handleBulkRestore = useCallback(
    data => {
      dispatch(restoreDocuments(data));
    },
    [dispatch],
  );

  const NameCell = ({ original }) => {
    // eslint-disable-line
    if (!original || !original.folder) {
      return <DocumentLink document={original} />;
    }
    const folder = foldersById[original.folder];
    if (!folder) {
      return <DocumentLink document={original} />;
    }
    const paths = folder.pathIds.map(id => ({
      path: foldersById[id].route,
      label: foldersById[id].name,
    }));
    return (
      <Flex flexDirection="column">
        <Breadcrumb paths={paths} />
        <DocumentLink document={original} />
      </Flex>
    );
  };

  const columns = [
    {
      Header: 'Name',
      accessor: 'name',
      Cell: NameCell,
      showOnMobile: true,
    },
    {
      Header: <RestoreIcon color={theme.colors.medium} />,
      Cell: (
        { original }, // eslint-disable-line
      ) => (
        <RowActionButton original={original} handleClick={handleRestore}>
          <RestoreIcon color={theme.colors.medium} />
        </RowActionButton>
      ),
      sortable: false,
      width: 50,
    },
  ];

  return (
    <>
      <Header>
        <MainTitle>
          <DeleteIcon inlineLeft /> Deleted documents in
          {space && space.name}
        </MainTitle>
      </Header>

      <CheckboxDataTable
        data={documents}
        columns={columns}
        bulkActions={({ selectedData }) => (
          <Flex>
            <Button variant="negative" onClick={() => handleBulkRestore(selectedData)}>
              <RestoreIcon color={theme.colors.white} /> Restore
            </Button>
          </Flex>
        )}
      />
    </>
  );
}
